import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, ChevronDown, ChevronUp, X } from 'lucide-react';
import apiClient from '../lib/api';

const CONSENT_KEY = 'zivu_cookie_consent';

// Default hardcoded categories — used as fallback if backend isn't live yet
const DEFAULT_CATEGORIES = [
  {
    id: 'necessary',
    label: 'Strictly Necessary',
    description: 'Essential for the website to function. Includes session cookies, authentication, cart, and security tokens.',
    required: true,
  },
  {
    id: 'functional',
    label: 'Functional',
    description: 'Remembers your preferences such as language, currency, theme, and recently viewed items.',
    required: false,
  },
  {
    id: 'analytics',
    label: 'Analytics',
    description: 'Helps us understand how visitors interact with the website to improve user experience.',
    required: false,
  },
  {
    id: 'marketing',
    label: 'Marketing',
    description: 'Used to deliver relevant advertisements and track campaign effectiveness across platforms.',
    required: false,
  },
];

function getStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function storeConsent(categories, version) {
  const data = {
    version: version || '1',
    timestamp: new Date().toISOString(),
    categories,
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(data));
  // Notify tracking-loader.js so it can inject GTM / FB Pixel / etc.
  // immediately after the user accepts (no page reload required).
  try {
    window.dispatchEvent(new CustomEvent('zivu:consent-changed', { detail: { categories } }));
  } catch (_e) {
    /* IE/legacy fallback — old CustomEvent constructor unavailable */
  }
}

/** Normalise a backend category object to the shape used internally */
function normaliseCategory(cat) {
  return {
    id: cat.slug || cat.id,
    label: cat.name || cat.label,
    description: cat.description || '',
    required: cat.is_required ?? cat.required ?? false,
  };
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [consentVersion, setConsentVersion] = useState('1');
  const [selections, setSelections] = useState(() => {
    const defaults = {};
    DEFAULT_CATEGORIES.forEach((c) => { defaults[c.id] = c.required; });
    return defaults;
  });
  const fetchedRef = useRef(false);

  // Fetch categories from backend once on mount
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    apiClient
      .get('/public/cookie-categories?locale=en')
      .then((res) => {
        const serverVersion = String(res.data?.consent_version || '1');
        const serverCats = (res.data?.categories || []).map(normaliseCategory);
        if (serverCats.length > 0) {
          setCategories(serverCats);
          setConsentVersion(serverVersion);
          // Re-initialise selections with server defaults
          const defaults = {};
          serverCats.forEach((c) => { defaults[c.id] = c.required || c.default_enabled || false; });
          setSelections(defaults);
        }
      })
      .catch(() => {
        // Backend not live — keep DEFAULT_CATEGORIES
      });
  }, []);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored || stored.version !== consentVersion) {
      // No consent stored, or server bumped the version — re-prompt
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, [consentVersion]);

  // Listen for global `zivu:openCookiePreferences` event so footer links /
  // account settings can re-open the banner at any time (GDPR Art. 7 —
  // withdrawal must be as easy as granting).
  useEffect(() => {
    const handler = () => {
      const stored = getStoredConsent();
      if (stored?.categories) {
        // Preload current selections so user can toggle
        setSelections(stored.categories);
      }
      setExpanded(true);
      setVisible(true);
    };
    window.addEventListener('zivu:openCookiePreferences', handler);
    return () => window.removeEventListener('zivu:openCookiePreferences', handler);
  }, []);

  const handleAcceptAll = useCallback(() => {
    const all = {};
    categories.forEach((c) => { all[c.id] = true; });
    storeConsent(all, consentVersion);
    setVisible(false);
    // Gracefully POST consent record — skip if endpoint not live
    apiClient.post('/public/consent', { categories: all, version: consentVersion }).catch(() => {});
  }, [categories, consentVersion]);

  const handleRejectNonEssential = useCallback(() => {
    const minimal = {};
    categories.forEach((c) => { minimal[c.id] = c.required; });
    storeConsent(minimal, consentVersion);
    setVisible(false);
    apiClient.post('/public/consent', { categories: minimal, version: consentVersion }).catch(() => {});
  }, [categories, consentVersion]);

  const handleSavePreferences = useCallback(() => {
    storeConsent(selections, consentVersion);
    setVisible(false);
    apiClient.post('/public/consent', { categories: selections, version: consentVersion }).catch(() => {});
  }, [selections, consentVersion]);

  const toggleCategory = (id) => {
    const cat = categories.find((c) => c.id === id);
    if (cat?.required) return;
    setSelections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            aria-hidden="true"
          />

          {/* Banner */}
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6"
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            role="dialog"
            aria-label="Cookie consent"
            aria-modal="true"
          >
            <div className="max-w-3xl mx-auto bg-[#0C0C0E] border-2 border-zinc-800 rounded-lg shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="p-5 sm:p-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-zinc-800 rounded-lg shrink-0 mt-0.5">
                    <Shield className="h-5 w-5 text-[#FC0000]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-['Barlow_Condensed',sans-serif] text-lg font-bold uppercase tracking-wide text-zinc-100">
                      Your Privacy Matters
                    </h2>
                    <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">
                      We use cookies to ensure our website works properly, improve your experience, and deliver relevant content.
                      You can choose which cookies to allow.{' '}
                      <Link to="/privacy" className="text-[#FC0000] hover:underline" onClick={() => setVisible(false)}>
                        Privacy Policy
                      </Link>
                    </p>
                  </div>
                </div>

                {/* Expandable categories */}
                <AnimatePresence>
                  {expanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-5 space-y-2">
                        {categories.map((cat) => (
                          <div
                            key={cat.id}
                            className="flex items-start gap-3 p-3 bg-zinc-900/60 border border-zinc-800 rounded-lg"
                          >
                            {/* Toggle */}
                            <button
                              type="button"
                              role="switch"
                              aria-checked={selections[cat.id]}
                              aria-label={`${cat.label} cookies`}
                              disabled={cat.required}
                              onClick={() => toggleCategory(cat.id)}
                              className={`mt-0.5 relative shrink-0 inline-flex h-5 w-9 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FC0000] ${
                                selections[cat.id]
                                  ? 'bg-[#FC0000]'
                                  : 'bg-zinc-700'
                              } ${cat.required ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                              <span
                                className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
                                  selections[cat.id] ? 'translate-x-[18px]' : 'translate-x-[3px]'
                                }`}
                              />
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-zinc-200">{cat.label}</span>
                                {cat.required && (
                                  <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded">
                                    Always on
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{cat.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Actions */}
                <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
                  <button
                    onClick={handleAcceptAll}
                    aria-label="Accept all cookies"
                    className="px-5 py-2.5 bg-[#C00000] hover:bg-[#A30000] text-white text-sm font-bold uppercase tracking-wider rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0C0C0E]"
                  >
                    Accept All
                  </button>

                  {expanded ? (
                    <button
                      onClick={handleSavePreferences}
                      className="px-5 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm font-bold uppercase tracking-wider rounded-lg border border-zinc-700 transition-colors"
                    >
                      Save Preferences
                    </button>
                  ) : (
                    <button
                      onClick={handleRejectNonEssential}
                      className="px-5 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm font-bold uppercase tracking-wider rounded-lg border border-zinc-700 transition-colors"
                    >
                      Necessary Only
                    </button>
                  )}

                  <button
                    onClick={() => setExpanded((prev) => !prev)}
                    className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm text-zinc-400 hover:text-zinc-200 transition-colors sm:ml-auto"
                  >
                    {expanded ? (
                      <>
                        Hide Options <ChevronDown className="h-3.5 w-3.5" />
                      </>
                    ) : (
                      <>
                        Customize <ChevronUp className="h-3.5 w-3.5" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/**
 * Hook to read current cookie consent preferences.
 * Returns null if no consent given yet, or { necessary, functional, analytics, marketing }.
 */
export function useCookieConsent() {
  const [consent, setConsent] = useState(() => {
    const stored = getStoredConsent();
    return stored ? stored.categories : null;
  });

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === CONSENT_KEY) {
        const stored = getStoredConsent();
        setConsent(stored ? stored.categories : null);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return consent;
}
